import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { useTitle } from "../lib/seo";
import { Pricing } from "../components/Pricing";
import { RedeemCode, CouponAdmin } from "../components/Coupons";
import { Progress } from "../components/ui/progress";

const Stat = ({ label, value, testid }) => (
  <div className="panel p-6"><p className="text-xs text-zinc-500 font-mono-tc uppercase tracking-wider">{label}</p><p className="mt-2 text-2xl font-display font-bold text-white" data-testid={testid}>{value}</p></div>
);

const UserRow = ({ u, onDone }) => {
  const [plan, setPlan] = useState(u.plan || "gratis");
  const grant = async () => {
    try { await api.post(`/admin/users/${u.id}/plan`, { plan, days: 365 }); toast.success(`Plan ${plan} asignado a ${u.email}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-3 items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`admin-user-${u.id}`}>
      <span className="text-zinc-200">{u.email}{u.role === "admin" && <span className="ml-2 text-[10px] font-mono-tc text-[#E8C47C]">DEV</span>}</span>
      {u.role !== "admin" && (
        <div className="flex gap-2 items-center">
          <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid={`admin-plan-select-${u.id}`} className="bg-zinc-900 border border-white/10 rounded-md text-xs px-2 py-1.5">
            {["gratis", "creador", "pro", "agencia"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-8 border-white/10" data-testid={`admin-grant-btn-${u.id}`} onClick={grant}>Asignar gratis</Button>
        </div>
      )}
    </div>
  );
};

const PlanPriceAdmin = () => {
  const [plans, setPlans] = useState([]);
  const [discount, setDiscount] = useState("15");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/admin/plans"),
      api.get("/admin/annual-discount")
    ]).then(([p, d]) => {
      setPlans(p.data);
      setDiscount(String(d.data.percent));
    }).catch((e) => {
      toast.error(errMsg(e));
    }).finally(() => setLoading(false));
  }, []);

  const savePlan = async (p) => {
    const monthly = Number(p.monthly);
    if (!Number.isFinite(monthly) || monthly < 0) {
      toast.error("Introduce un precio mensual v?lido.");
      return;
    }
    setSaving(p.id);
    try {
      const r = await api.put(`/admin/plans/${p.id}`, { monthly });
      setPlans((old) => old.map((x) => x.id === p.id ? { ...x, ...r.data.plan } : x));
      toast.success(`Precio de ${p.name} actualizado.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving("");
    }
  };

  const saveDiscount = async () => {
    const percent = Number(discount);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      toast.error("El porcentaje debe estar entre 0 y 100.");
      return;
    }
    setSaving("discount");
    try {
      const r = await api.put("/admin/annual-discount", { percent });
      setDiscount(String(r.data.percent));
      toast.success("Promoci?n anual actualizada.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving("");
    }
  };

  if (loading) {
    return <div className="panel mt-4 p-5 text-sm text-zinc-400">Cargando precios...</div>;
  }

  return (
    <div className="panel mt-4 p-5" data-testid="admin-plan-prices">
      <h3 className="text-lg font-bold text-white mb-2">Editar precios y promociones</h3>
      <p className="text-sm text-zinc-400 mb-5">
        Modifica los precios mensuales y el descuento aplicado a la contrataci?n anual.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {plans.map((p) => {
          const annual = Number(p.monthly || 0) * 12 * (1 - Number(discount || 0) / 100);
          return (
            <div key={p.id} className="rounded-xl border border-white/10 p-4">
              <h4 className="font-semibold text-white mb-3">{p.name}</h4>
              <label className="block text-xs text-zinc-400 mb-1">Precio mensual (USD)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={p.monthly ?? 0}
                  disabled={p.id === "gratis"}
                  onChange={(e) => setPlans((old) => old.map((x) =>
                    x.id === p.id ? { ...x, monthly: e.target.value } : x
                  ))}
                  className="min-w-0 flex-1 rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-white disabled:opacity-50"
                  data-testid={`admin-monthly-${p.id}`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={saving === p.id || p.id === "gratis"}
                  onClick={() => savePlan(p)}
                  className="border-white/10"
                >
                  {saving === p.id ? "Guardando..." : "Guardar"}
                </Button>
              </div>
              <p className="text-xs text-zinc-400 mt-3">
                Total anual con promoci?n: <span className="text-white">{money(annual)}</span>
              </p>
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/10 pt-5">
        <label className="block text-sm text-zinc-300 mb-2">Promoci?n anual global (%)</label>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            className="w-32 rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-white"
            data-testid="admin-annual-discount"
          />
          <Button
            size="sm"
            disabled={saving === "discount"}
            onClick={saveDiscount}
            className="bg-[#E8C47C] text-black hover:bg-[#F3D9A4]"
          >
            {saving === "discount" ? "Guardando..." : "Guardar promoci?n"}
          </Button>
          <span className="text-xs text-zinc-500">Usa 0 para desactivar la promoci?n.</span>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [s, setS] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/stats").then((r) => setS(r.data));

  useEffect(() => { load(); }, []);

  const createTestPayments = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/payments/test-batch");
      toast.success(`${r.data.created} intentos de prueba registrados. No hubo cargos.`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  if (!s) return null;

  return (
    <section className="mt-14" data-testid="admin-panel">
      <h2 className="text-xl font-bold text-white mb-5">Panel de administración</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Usuarios" value={s.users} testid="admin-users" />
        <Stat label="Videos" value={s.videos} testid="admin-videos" />
        <Stat label="Pagos completados" value={s.payments} testid="admin-payments" />
        <Stat label="Ingresos reales" value={money(s.revenue)} testid="admin-revenue" />
        <Stat label="Pruebas registradas" value={s.test_attempts || 0} testid="admin-test-payments" />
      </div>

      <div className="panel mt-4 p-5">
        <h3 className="text-white font-semibold mb-2">Pruebas de paquetes</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Registra los seis intentos: Creador, Pro Studio y Agencia, en modalidad mensual y anual.
          Son operaciones internas: no generan órdenes PayPal, cargos ni activaciones de planes.
        </p>
        <Button
          onClick={createTestPayments}
          disabled={testing}
          className="bg-[#E8C47C] text-black hover:bg-[#F3D9A4]"
          data-testid="admin-create-payment-tests"
        >
          {testing ? "Registrando pruebas..." : "Registrar las 6 pruebas de pago"}
        </Button>
      </div>

      <div className="panel mt-4 p-5 text-sm">
        <p className="text-[#E8C47C] mb-1">Cuenta de desarrollador: acceso ilimitado (plan Agencia) sin pago.</p>
        <p className="text-zinc-400 mb-3">Usuarios · asigna cualquier plan gratis (cortesía, 1 año)</p>
        {s.recent_users.map((u) => <UserRow key={u.id} u={u} onDone={load} />)}
      </div>

      <div className="panel mt-4 p-5" data-testid="admin-payment-history">
        <h3 className="text-white font-semibold mb-4">Historial de pagos e intentos</h3>
        {!s.recent_payments?.length ? (
          <p className="text-sm text-zinc-500">Todavía no hay operaciones registradas.</p>
        ) : (
          <div className="space-y-3">
            {s.recent_payments.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-0">
                <div>
                  <p className="text-sm text-zinc-200 capitalize">
                    {p.plan} · {p.cycle === "annual" ? "Anual" : "Mensual"}
                  </p>
                  <p className="text-xs text-zinc-500">{p.email}</p>
                  <p className="text-xs text-zinc-500">
                    {p.is_test ? "Prueba interna" : p.provider === "paypal" ? "PayPal" : p.provider || "Otro método"}
                    {" · "}
                    {p.created_at ? new Date(p.created_at).toLocaleString("es") : "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white font-mono-tc">{money(p.amount)}</p>
                  <p className={`text-xs font-mono-tc ${p.payment_status === "paid" && !p.is_test ? "text-emerald-400" : "text-amber-400"}`}>
                    {p.payment_status === "paid" && !p.is_test ? "PAGADO" : (p.payment_status || "PENDING").toUpperCase()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PlanPriceAdmin />
      <CouponAdmin />
    </section>
  );
};

export default function Account() {
  useTitle("Mi cuenta · AR Studio Creative");
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [walletHistory, setWalletHistory] = useState([]);
  const [topupAmount, setTopupAmount] = useState("10");
  const [topupLoading, setTopupLoading] = useState(false);

  useEffect(() => {
    api.get("/account/usage").then((r) => setUsage(r.data));
    api.get("/payments/history").then((r) => setHistory(r.data));
    api.get("/wallet").then((r) => setWallet(r.data));
    api.get("/wallet/history").then((r) => setWalletHistory(r.data));
  }, []);

  const createTopup = async () => {
    const amount = Number(topupAmount);
    if (!Number.isFinite(amount) || amount < 1 || Math.round(amount * 100) !== amount * 100) {
      toast.error("Introduce un importe válido desde $1.00, con máximo dos decimales.");
      return;
    }
    setTopupLoading(true);
    try {
      const r = await api.post("/wallet/paypal/create", {
        amount_cents: Math.round(amount * 100),
        origin_url: window.location.origin
      });
      if (!r.data?.url) throw new Error("PayPal no devolvió el enlace de pago.");
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(errMsg(e));
      setTopupLoading(false);
    }
  };

  const pct = usage?.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 5;

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="account-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <h1 className="text-3xl font-extrabold text-white">Hola, {user?.name}</h1>
        <p className="text-zinc-400 text-sm mt-1">{user?.email}</p>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          <Stat label="Plan actual" value={usage?.plan_name || "…"} testid="account-plan" />
          <div className="panel p-6">
            <p className="text-xs text-zinc-500 font-mono-tc uppercase tracking-wider">Videos {usage?.plan === "gratis" ? "de prueba" : "este mes"}</p>
            <p className="mt-2 text-2xl font-display font-bold text-white" data-testid="account-usage">{usage ? `${usage.used} / ${usage.limit ?? "∞"}` : "…"}</p>
            <Progress value={pct} className="h-1.5 mt-3" />
          </div>
          <Stat label="Renovación" value={user?.plan_expires_at && user.plan !== "gratis" ? new Date(user.plan_expires_at).toLocaleDateString("es") : "—"} testid="account-expiry" />
        </div>
        <div className="mt-4"><RedeemCode onDone={() => api.get("/account/usage").then((r) => setUsage(r.data))} /></div>

        <section id="wallet-topup" className="panel mt-10 p-6" data-testid="wallet-topup">
          <h2 className="text-xl font-bold text-white">Saldo de créditos</h2>
          <p className="text-sm text-zinc-400 mt-1">Cada dólar recargado equivale a un dólar de saldo.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {[
              ["Disponible", wallet?.available],
              ["Saldo total", wallet?.balance],
              ["Reservado", wallet?.reserved]
            ].map(([label,value]) => <div key={label} className="rounded-xl bg-zinc-900 p-4">
              <p className="text-xs text-zinc-500">{label}</p>
              <p className="text-2xl font-bold text-white mt-1">{value == null ? "…" : money(value)}</p>
            </div>)}
          </div>
          <h3 className="text-white font-semibold mt-6 mb-3">Recargar saldo</h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {[5,10,15,20,50,100].map(n => <Button key={n} type="button"
              variant={Number(topupAmount)===n ? "default":"outline"}
              onClick={()=>setTopupAmount(String(n))}
              className={Number(topupAmount)===n ? "bg-[#E8C47C] text-black":"border-white/15 text-white"}>${n}</Button>)}
          </div>
          <div className="flex flex-wrap gap-3 items-end mt-4">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-zinc-400 mb-2">Importe personalizado (USD)</label>
              <input type="number" min="1" step="0.01" value={topupAmount}
                onChange={e=>setTopupAmount(e.target.value)}
                className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2.5 text-white"/>
            </div>
            <Button onClick={createTopup} disabled={topupLoading}
              className="bg-[#E8C47C] text-black">
              {topupLoading ? "Conectando..." : "Recargar con PayPal"}
            </Button>
          </div>
          <h3 className="text-white font-semibold mt-6 mb-3">Movimientos recientes</h3>
          {walletHistory.slice(0,8).map(h=><div key={h.transaction_id}
            className="flex justify-between border-b border-white/5 py-2 text-sm">
            <span className="text-zinc-300">{h.type==="credit_topup" ? "Recarga PayPal":"Generación de video"}</span>
            <span className="text-white">{money((h.amount_cents||0)/100)}</span>
          </div>)}
        </section>
        <section className="mt-14">
          <h2 className="text-xl font-bold text-white mb-5">Historial de pagos</h2>
          <div className="panel divide-y divide-white/5" data-testid="payment-history">
            {history.length === 0 ? <p className="p-6 text-sm text-zinc-500">Sin pagos todavía.</p> : history.map((h) => (
              <div key={h.id} className="p-4 flex flex-wrap gap-4 justify-between text-sm">
                <span className="text-zinc-200 capitalize">{h.plan} · {h.cycle === "annual" ? "Anual" : "Mensual"}</span>
                <span className="text-zinc-500">{h.is_test ? "Prueba interna" : h.provider === "paypal" ? "PayPal" : h.provider === "coupon" ? `Código ${h.coupon}` : "Pago anterior"}</span>
                <span className="text-zinc-500">{new Date(h.created_at).toLocaleDateString("es")}</span>
                <span className="font-mono-tc text-white">{money(h.amount)}</span>
                <span className={`text-xs font-mono-tc ${h.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>{h.payment_status === "paid" ? "PAGADO" : h.payment_status.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="mt-14"><h2 className="text-xl font-bold text-white mb-8">Cambiar de plan</h2><Pricing /></section>
        {user?.role === "admin" && <AdminPanel />}
      </div>
    </div>
  );
}
